<template>
  <v-card outlined class="my-3" data-testid="nachweis-pruefung">
    <v-card-title class="subtitle-1">Belege verwalten</v-card-title>
    <v-card-text>
      <p v-if="targetType === 'order_document'" class="text-caption">
        Die Angaben gelten nur für diese Rechnung / Abrechnungsperiode.
      </p>
      <NachweisStatus :evidence="stand" />
      <v-alert v-if="fehler" type="error" dense text role="alert">{{ fehler }}</v-alert>
      <v-alert v-if="erfolg" type="success" dense text role="status">{{ erfolg }}</v-alert>
      <v-progress-linear v-if="laden" indeterminate />
      <v-btn small text :disabled="speichern" :loading="laden" @click="ladeStand()">
        Stand aktualisieren
      </v-btn>
      <template v-if="stand">
        <v-select v-model="art" :items="arten" label="Nachweisart" :disabled="speichern" />
        <div v-if="letzteAenderung.updatedAt" class="text-caption mb-2">
          Letzte Änderung: {{ letzteAenderung.updatedAt }}
          · {{ akteur(letzteAenderung.actor) }}
          <span v-if="letzteAenderung.checkedAt"> · Geprüft: {{ letzteAenderung.checkedAt }}</span>
        </div>
        <template v-if="art !== 'invoice_requirement'">
          <v-select
            v-model="formular.availability" :items="ablagearten" label="Belegablage"
            :disabled="speichern" data-testid="nachweis-verfuegbarkeit"
            hint="Ein vorhandener Beleg muss hier nicht hochgeladen oder zusätzlich geprüft werden."
            persistent-hint class="mb-3"
          />
          <v-select
            v-model="formular.documentId" :items="zuordenbareDateien" clearable
            label="Datei zuordnen (optional)" :disabled="speichern"
            hint="Bei Verträgen die passende Bankdatei dieser Rechnung zuordnen. Ohne Upload: extern vorhanden wählen."
            persistent-hint class="mb-3"
          />
          <v-select
            v-model="formular.sourceType" :items="quellen" label="Quelle (optional)" clearable
            :disabled="speichern" data-testid="nachweis-quelle"
          />
          <v-text-field
            v-model="formular.sourceReference" label="Referenz / Belegnummer / Ablageort (optional)"
            maxlength="255" counter :disabled="speichern"
            hint="Nur die zugehörige Referenz, keine Zugangsdaten oder Auftragsbeschreibung."
          />
          <v-textarea
            v-model="formular.note" :label="formular.status === 'verified' ? 'Prüfvermerk' : 'Notiz (optional)'" rows="2" auto-grow
            maxlength="1000" counter :disabled="speichern"
          />
          <v-alert v-if="formular.sourceType === 'preliminary'" dense text type="warning">
            Eine Bestellübersicht ist vorläufig. Die Originalrechnung bleibt offen;
            eine abgeschlossene Prüfung kann damit nicht bestätigt werden.
          </v-alert>
        </template>
        <v-select
          v-model="formular.status" :items="zustaende"
          :label="art === 'invoice_requirement' ? 'Rechnungserfordernis' : 'Optionale Prüfung'"
          :disabled="speichern" data-testid="nachweis-zustand"
        />
        <v-textarea
          v-if="grundErforderlich || art === 'invoice_requirement'"
          v-model="formular.reason" label="Begründung (bei Widerruf / Ausnahme erforderlich)"
          rows="2" auto-grow maxlength="1000" counter :disabled="speichern"
        />
        <p v-if="art === 'invoice_requirement'" class="text-caption">
          Beispielsweise für Finanzamt-Kosten ohne Lieferantenrechnung:
          „Rechnung nicht erforderlich“ mit Begründung speichern.
          Eine Bank- oder Steuerreferenz kann separat hinterlegt werden.
        </p>
        <p class="text-caption">
          Die Schaltfläche speichert die Belegangabe sofort, auch ohne PDF und ohne Prüfung.
          Sie erzeugt weder eine Datei noch eine Zahlung oder eine DATEV-Übertragung.
          Nutzer und Zeitpunkt werden gespeichert. Zahlungen bitte separat mit Betrag und Datum erfassen.
        </p>
        <v-btn
          color="primary" small :loading="speichern" :disabled="!gueltig || laden"
          @click="speicherePruefung()" data-testid="nachweis-speichern"
        >{{ formular.status === 'verified' ? 'Optionale Prüfung bestätigen' : 'Belegangabe speichern' }}</v-btn>
        <v-expansion-panels v-if="stand.audit && stand.audit.length" flat class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header>Änderungsverlauf ({{ stand.audit.length }})</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="eintrag in stand.audit" :key="eintrag.rowVersion" class="mb-4 text-caption">
                <strong>{{ artName(eintrag.kind) }}: {{ statusName(eintrag.before.status) }} → {{ statusName(eintrag.after.status) }}</strong>
                <div v-if="eintrag.after.availability === 'external'">Beleg extern vorhanden (ohne Upload)</div>
                <div>{{ eintrag.occurredAt }} · {{ akteur(eintrag.actor) }}</div>
                <div v-if="eintrag.after.sourceReference">Referenz: {{ eintrag.after.sourceReference }}</div>
                <div v-if="eintrag.after.note">Notiz: {{ eintrag.after.note }}</div>
                <div v-if="eintrag.after.reason">Begründung: {{ eintrag.after.reason }}</div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import $ from 'jquery';
import NachweisStatus from './NachweisStatus.vue';
import { sichereApiFehlermeldung } from '@/utils/apiFehler';
import { cacheNachweisAktualisieren } from '@/utils/nachweisStatus';

export default {
  components: { NachweisStatus },
  props: {
    targetType: { type: String, required: true },
    targetId: { type: [Number, String], required: true },
    evidence: { type: Object, default: null },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    stand: null,
    art: 'content',
    formular: {},
    laden: false,
    ladeSequenz: 0,
    speichern: false,
    fehler: '',
    erfolg: '',
    wiederholung: null,
    arten: [
      { text: 'Rechnungsbeleg', value: 'content' },
      { text: 'Bank-Buchungsbeleg / Zahlungsnachweis', value: 'bank' },
      { text: 'Rechnungserfordernis', value: 'invoice_requirement' },
    ],
    ablagearten: [
      { text: 'Angehängte Datei verwenden / noch nicht vorhanden', value: 'unknown' },
      { text: 'Extern vorhanden (ohne Upload)', value: 'external' },
    ],
  }),
  computed: {
    zuordenbareDateien() {
      return ((this.stand && this.stand.availableDocuments) || [])
        .filter((d) => d.hasContent && Number(d.documentType) === (this.art === 'bank' ? 20 : 1))
        .map((d) => ({ text: `${d.name || 'Dokument'} (ID ${d.id})`, value: d.id }));
    },
    letzteAenderung() {
      return (this.stand && this.stand[this.art === 'invoice_requirement' ? 'invoiceRequirement' : this.art]) || {};
    },
    quellen() {
      return [
        { text: 'Rechnung', value: 'invoice' },
        { text: 'Bank-Buchungsbeleg / Kontoauszug', value: 'bank' },
        { text: 'Steuerreferenz / Bescheid', value: 'tax_reference' },
        { text: 'Bestellübersicht / vorläufig', value: 'preliminary' },
        { text: 'Andere Quelle', value: 'other' },
      ];
    },
    zustaende() {
      return this.art === 'invoice_requirement' ? [
        { text: 'Rechnung erforderlich', value: 'required' },
        { text: 'Rechnung nicht erforderlich', value: 'not_required' },
      ] : [
        { text: 'Keine zusätzliche Prüfung', value: 'open' },
        { text: 'Optional geprüft (auch ohne Datei)', value: 'verified' },
      ];
    },
    grundErforderlich() {
      return (this.letzteAenderung.status === 'verified' && this.formular.status === 'open')
        || (this.letzteAenderung.status === 'not_required' && this.formular.status === 'required')
        || this.formular.status === 'not_required';
    },
    gueltig() {
      const f = this.formular;
      const hatText = (wert) => typeof wert === 'string' && wert.trim().length > 0;
      if (this.grundErforderlich && !hatText(f.reason)) return false;
      if (this.art === 'invoice_requirement') return true;
      if (f.availability === 'external' && f.sourceType === 'preliminary') return false;
      if (f.status === 'verified') {
        return f.sourceType !== 'preliminary' && hatText(f.sourceType)
          && hatText(f.sourceReference) && hatText(f.note);
      }
      return f.status === 'open';
    },
  },
  watch: {
    art() { this.formularLaden(); },
    refreshKey() { this.ladeStand(); },
    targetId() { this.ladeStand(); },
  },
  methods: {
    artName(kind) {
      const art = this.arten.find((a) => a.value === kind);
      return art ? art.text : kind;
    },
    statusName(status) {
      return { verified: 'Optional geprüft', open: 'Keine zusätzliche Prüfung', required: 'Rechnung erforderlich', not_required: 'Rechnung nicht erforderlich' }[status] || status;
    },
    akteur(actor) {
      return actor ? `${actor.type === 'user' || actor.type === 'session' ? 'Nutzer' : 'System / API'} ${actor.id}` : 'Server';
    },
    formularLaden() {
      const stand = this.letzteAenderung;
      this.formular = {
        status: stand.status || (this.art === 'invoice_requirement' ? 'required' : 'open'),
        sourceType: stand.sourceType || '',
        sourceReference: stand.sourceReference || '',
        note: stand.note || '',
        documentId: stand.documentId || null,
        availability: stand.availability || 'unknown',
        reason: '',
      };
      this.wiederholung = null;
    },
    async anfrage(payload) {
      return $.ajax({
        url: `${process.env.VUE_APP_API_HOST}/400/410/`,
        type: 'post', dataType: 'json', timeout: 20000,
        data: {
          code: this.$store.state.login.session.code,
          dataToSave: JSON.stringify({ ...payload, targetType: this.targetType, targetId: Number(this.targetId) }),
        },
      });
    },
    uebernehmeAntwort(response, eigenesSpeichern = false) {
      if (response && response.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        return false;
      }
      if (!response || response.status !== 'success' || !response.evidence) {
        this.fehler = sichereApiFehlermeldung(response && response.message, 'Der Nachweisstand konnte nicht verarbeitet werden.');
        return false;
      }
      this.stand = response.evidence;
      // Lesen und Replay bestaetigen keine eigene neue Auftragsrevision.
      // Dokumentanzeige und Belegstatus duerfen dennoch aktualisiert werden.
      const bestaetigung = eigenesSpeichern && response.idempotentReplay !== true
        ? response : { ...response, settlement: null };
      cacheNachweisAktualisieren(bestaetigung);
      this.formularLaden();
      this.$emit('changed', bestaetigung);
      return true;
    },
    async ladeStand() {
      if (this.speichern) return;
      const sequenz = this.ladeSequenz + 1;
      this.ladeSequenz = sequenz;
      this.laden = true;
      try {
        const response = await this.anfrage({ type: 'getEvidence' });
        if (sequenz !== this.ladeSequenz) return;
        if (this.uebernehmeAntwort(response)) this.fehler = '';
      } catch (error) {
        if (sequenz === this.ladeSequenz) this.fehler = 'Der aktuelle Nachweisstand konnte nicht geladen werden. Bitte erneut versuchen.';
      } finally {
        if (sequenz === this.ladeSequenz) this.laden = false;
      }
    },
    async speicherePruefung() {
      if (!this.gueltig || this.speichern) return;
      this.speichern = true;
      this.fehler = '';
      this.erfolg = '';
      const payload = { type: 'updateEvidence', rowVersion: this.stand.rowVersion, kind: this.art, ...this.formular };
      if (!payload.documentId || this.art === 'invoice_requirement') delete payload.documentId;
      if (this.art === 'invoice_requirement') delete payload.availability;
      if (!payload.sourceType) payload.sourceType = '';
      const signatur = JSON.stringify(payload);
      if (!this.wiederholung || this.wiederholung.signatur !== signatur) {
        this.wiederholung = { signatur, key: `evidence-${Date.now()}-${Math.random().toString(36).slice(2)}` };
      }
      payload.idempotencyKey = this.wiederholung.key;
      let konflikt = false;
      let wiederholt = false;
      try {
        const response = await this.anfrage(payload);
        konflikt = response && (response.status === 'conflict' || response.code === 'version_conflict');
        wiederholt = response && response.status === 'success' && response.idempotentReplay === true;
        // Ein Replay enthaelt die damalige Antwort. Niemals einen spaeteren Widerruf damit ersetzen.
        if (!wiederholt && this.uebernehmeAntwort(response, true)) this.erfolg = 'Der Nachweisstand wurde gespeichert.';
      } catch (error) {
        konflikt = error.status === 409;
        const response = error.responseJSON;
        if (response && response.status === 'accessError') this.$store.commit('login/backendErrorSession');
        this.fehler = sichereApiFehlermeldung(response && response.message,
          'Keine eindeutige Serverantwort. Stand aktualisieren oder denselben Vorgang erneut senden.');
      } finally {
        this.speichern = false;
      }
      if (konflikt) {
        await this.ladeStand();
        this.fehler = 'Zwischenzeitlich geändert: Der aktuelle Stand wurde geladen. Bitte vergleichen und erneut speichern.';
      } else if (wiederholt) {
        await this.ladeStand();
        if (!this.fehler) this.erfolg = 'Diese Bestätigung wurde bereits verarbeitet. Der aktuelle Nachweisstand wurde geladen.';
      }
    },
  },
  created() { this.ladeStand(); },
};
</script>
